import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BankAccount,
  PaymentRequestListItem,
  PaymentRequestSpentItem,
  getBankAccounts,
  getPaymentRequestSpent,
  getPendingIssuePaymentRequests,
  issuePaymentRequests,
  rejectIssuePaymentRequests,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import { SortState, compareValues, formatContractSumDisplay, formatDateShort, toggleSort } from '../utils/table';
import {
  formatSignedSum,
  getPaymentControlLight,
  spentLookupKey,
} from '../utils/paymentRequestFinance';
import { notifyPaymentApprovalsChanged } from '../utils/paymentApprovalsEvents';
import '../styles/DataPage.css';
import './PaymentRequestsViewPage.css';
import './PaymentApprovalsPage.css';
import './PaymentIssuesPage.css';

const DEFAULT_PAYMENT_ACCOUNT_ID = 14;

type SortKey =
  | 'request_date'
  | 'task_id'
  | 'task_short_name'
  | 'planfact_article'
  | 'note'
  | 'requestor_name'
  | 'amount'
  | 'payment_account';

type FilterMenuKey = 'article' | 'approver' | null;

interface ApproverGroup {
  approverId: number;
  approverName: string;
  rows: PaymentRequestListItem[];
  totalAmount: number;
}

function parseAmountBound(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

function parseFilterDay(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function dateInRange(value: string | null | undefined, from: string, to: string): boolean {
  const hasFrom = Boolean(from.trim());
  const hasTo = Boolean(to.trim());
  if (!hasFrom && !hasTo) return true;
  if (!value?.trim()) return false;

  const ts = parseFilterDay(value.slice(0, 10));
  if (ts === null) return true;

  const fromTs = hasFrom ? parseFilterDay(from) ?? Number.NEGATIVE_INFINITY : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? parseFilterDay(to) ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

function displayValue(value: string | null | undefined): string {
  return value?.trim() ? value : '—';
}

function resolveDefaultAccountId(accounts: BankAccount[]): number | null {
  if (accounts.some((account) => account.id === DEFAULT_PAYMENT_ACCOUNT_ID)) {
    return DEFAULT_PAYMENT_ACCOUNT_ID;
  }
  return accounts[0]?.id ?? null;
}

const PaymentIssuesPage: React.FC = () => {
  const { can } = useAuth();
  const canView = can('OUTCOME_ISSUE', 'EDIT');
  const canEdit = can('OUTCOME_ISSUE', 'EDIT');

  const [rows, setRows] = useState<PaymentRequestListItem[]>([]);
  const [spentByKey, setSpentByKey] = useState<Record<string, PaymentRequestSpentItem>>({});
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [accountByRowId, setAccountByRowId] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<FilterMenuKey>(null);
  const [searchText, setSearchText] = useState('');
  const [taskIdFilter, setTaskIdFilter] = useState('');
  const [filterArticles, setFilterArticles] = useState<string[]>([]);
  const [filterApprovers, setFilterApprovers] = useState<string[]>([]);
  const [filterRequestDateFrom, setFilterRequestDateFrom] = useState('');
  const [filterRequestDateTo, setFilterRequestDateTo] = useState('');
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(() => new Set());

  const [sortState, setSortState] = useState<SortState<SortKey>>({
    key: 'request_date',
    direction: 'desc',
  });

  const defaultAccountId = useMemo(() => resolveDefaultAccountId(bankAccounts), [bankAccounts]);

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canView) {
      setLoading(false);
      setRows([]);
      setBankAccounts([]);
      setAccountByRowId({});
      return;
    }

    setLoading(true);
    setError('');
    try {
      const [data, accounts] = await Promise.all([
        getPendingIssuePaymentRequests(),
        getBankAccounts(),
      ]);
      const fallbackAccountId = resolveDefaultAccountId(accounts);
      const nextAccounts: Record<number, number> = {};
      for (const row of data) {
        const preferred =
          row.payment_account
          && accounts.some((account) => account.id === row.payment_account)
            ? row.payment_account
            : fallbackAccountId;
        if (preferred != null) {
          nextAccounts[row.id] = preferred;
        }
      }
      setBankAccounts(accounts);
      setRows(data);
      setAccountByRowId(nextAccounts);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    const seen = new Set<string>();
    const items: Array<{ task_id: string; planfact_article: string }> = [];
    rows.forEach((row) => {
      const taskId = row.task_id.trim();
      const article = row.planfact_article.trim();
      if (!taskId || !article) {
        return;
      }
      const key = spentLookupKey(taskId, article);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      items.push({ task_id: taskId, planfact_article: article });
    });

    if (items.length === 0) {
      setSpentByKey({});
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const spentItems = await getPaymentRequestSpent(items);
        if (cancelled) {
          return;
        }
        const next: Record<string, PaymentRequestSpentItem> = {};
        spentItems.forEach((item) => {
          next[spentLookupKey(item.task_id, item.planfact_article)] = item;
        });
        setSpentByKey(next);
      } catch {
        if (!cancelled) {
          setSpentByKey({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rows]);

  const articleFilterOptions = useMemo(
    () => [...new Set(rows.map((row) => row.planfact_article))].sort((a, b) => a.localeCompare(b, 'ru')),
    [rows],
  );

  const approverFilterOptions = useMemo(
    () =>
      [...new Set(rows.map((row) => row.approver_name || '—'))].sort((a, b) =>
        a.localeCompare(b, 'ru'),
      ),
    [rows],
  );

  const hasActiveFilters = useMemo(
    () =>
      searchText.trim() !== ''
      || taskIdFilter.trim() !== ''
      || filterArticles.length > 0
      || filterApprovers.length > 0
      || filterRequestDateFrom.trim() !== ''
      || filterRequestDateTo.trim() !== ''
      || minAmountFilter.trim() !== ''
      || maxAmountFilter.trim() !== '',
    [
      searchText,
      taskIdFilter,
      filterArticles,
      filterApprovers,
      filterRequestDateFrom,
      filterRequestDateTo,
      minAmountFilter,
      maxAmountFilter,
    ],
  );

  const clearFilters = (): void => {
    setSearchText('');
    setTaskIdFilter('');
    setFilterArticles([]);
    setFilterApprovers([]);
    setFilterRequestDateFrom('');
    setFilterRequestDateTo('');
    setMinAmountFilter('');
    setMaxAmountFilter('');
  };

  const filteredRows = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    const sumFromN = parseAmountBound(minAmountFilter);
    const sumToN = parseAmountBound(maxAmountFilter);

    return rows.filter((row) => {
      const approverName = row.approver_name || '—';
      const requestorName = row.requestor_name || '—';
      const accountId = accountByRowId[row.id];
      const accountName =
        bankAccounts.find((account) => account.id === accountId)?.planfact_account ?? '';
      const searchable = [
        row.task_id,
        row.task_short_name,
        row.planfact_article,
        row.note,
        requestorName,
        approverName,
        accountName,
        row.request_date,
        String(row.amount),
        formatContractSumDisplay(row.amount),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const amount = Number(row.amount) || 0;

      return (
        (!text || searchable.includes(text))
        && (!taskIdFilter.trim() || row.task_id.toLowerCase().includes(taskIdFilter.trim().toLowerCase()))
        && (filterArticles.length === 0 || filterArticles.includes(row.planfact_article))
        && (filterApprovers.length === 0 || filterApprovers.includes(approverName))
        && dateInRange(row.request_date, filterRequestDateFrom, filterRequestDateTo)
        && (sumFromN === null || amount >= sumFromN)
        && (sumToN === null || amount <= sumToN)
      );
    });
  }, [
    rows,
    searchText,
    taskIdFilter,
    filterArticles,
    filterApprovers,
    filterRequestDateFrom,
    filterRequestDateTo,
    minAmountFilter,
    maxAmountFilter,
    accountByRowId,
    bankAccounts,
  ]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((left, right) => {
      const leftAccount =
        bankAccounts.find((account) => account.id === accountByRowId[left.id])?.planfact_account ?? '';
      const rightAccount =
        bankAccounts.find((account) => account.id === accountByRowId[right.id])?.planfact_account ?? '';
      const leftMap: Record<SortKey, string | number | undefined> = {
        request_date: left.request_date,
        task_id: left.task_id,
        task_short_name: left.task_short_name ?? '',
        planfact_article: left.planfact_article,
        note: left.note ?? '',
        requestor_name: left.requestor_name ?? '',
        amount: left.amount,
        payment_account: leftAccount,
      };
      const rightMap: Record<SortKey, string | number | undefined> = {
        request_date: right.request_date,
        task_id: right.task_id,
        task_short_name: right.task_short_name ?? '',
        planfact_article: right.planfact_article,
        note: right.note ?? '',
        requestor_name: right.requestor_name ?? '',
        amount: right.amount,
        payment_account: rightAccount,
      };
      const primary = compareValues(leftMap[sortState.key], rightMap[sortState.key], sortState.direction);
      if (primary !== 0) return primary;
      return (left.approver_name || '').localeCompare(right.approver_name || '', 'ru');
    });
    return sorted;
  }, [filteredRows, sortState, accountByRowId, bankAccounts]);

  const groups = useMemo((): ApproverGroup[] => {
    const map = new Map<number, ApproverGroup>();
    for (const row of sortedRows) {
      const approverId = row.approver_id ?? 0;
      const existing = map.get(approverId);
      if (existing) {
        existing.rows.push(row);
        existing.totalAmount += Number(row.amount) || 0;
      } else {
        map.set(approverId, {
          approverId,
          approverName: row.approver_name || '—',
          rows: [row],
          totalAmount: Number(row.amount) || 0,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.approverName.localeCompare(b.approverName, 'ru'),
    );
  }, [sortedRows]);

  const visibleIds = useMemo(() => sortedRows.map((row) => row.id), [sortedRows]);

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  const someVisibleSelected =
    visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  const totalAmount = useMemo(
    () => sortedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [sortedRows],
  );

  const handleSort = (key: SortKey): void => {
    setSortState((prev) => toggleSort(prev, key));
  };

  const toggleSelectAllVisible = (): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleRowSelected = (id: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroupSelected = (groupIds: number[]): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = groupIds.length > 0 && groupIds.every((id) => next.has(id));
      if (allSelected) {
        groupIds.forEach((id) => next.delete(id));
      } else {
        groupIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleGroupCollapsed = (approverId: number): void => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(approverId)) next.delete(approverId);
      else next.add(approverId);
      return next;
    });
  };

  const setRowAccount = (rowId: number, accountId: number): void => {
    setAccountByRowId((prev) => ({ ...prev, [rowId]: accountId }));
  };

  const handleIssue = async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Выберите хотя бы одну заявку.');
      return;
    }

    const missingAccount = ids.find((id) => accountByRowId[id] == null);
    if (missingAccount != null || bankAccounts.length === 0) {
      setError('Укажите счёт для каждой выбранной заявки.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess(null);
    try {
      const result = await issuePaymentRequests(
        ids.map((id) => ({ id, payment_account: accountByRowId[id] })),
      );
      const parts = [`Выплачено заявок: ${result.updated_count}`];
      if (result.synced_count > 0) {
        parts.push(`в ПланФакт: ${result.synced_count}`);
      }
      setSuccess(parts.join(', '));
      if (result.sync_errors.length > 0) {
        setError(
          `Не удалось отправить в ПланФакт (${result.sync_errors.length}): ${result.sync_errors.join('; ')}`,
        );
      }
      await fetchData();
      notifyPaymentApprovalsChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка выплаты');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Выберите хотя бы одну заявку.');
      return;
    }
    const reason = window.prompt('Причина отклонения (необязательно):') ?? undefined;
    if (reason === undefined) {
      return;
    }
    setSaving(true);
    setError('');
    setSuccess(null);
    try {
      const updated = await rejectIssuePaymentRequests(ids, reason);
      setSuccess(`Отклонено заявок: ${updated}`);
      await fetchData();
      notifyPaymentApprovalsChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка отклонения');
    } finally {
      setSaving(false);
    }
  };

  const colgroup = (
    <colgroup>
      <col className="payment-approvals-page__colgrid payment-approvals-page__colgrid--check" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--date" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--task-id" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--task-name" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--article" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--note" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--amount" />
      <col className="payment-approvals-page__colgrid payment-approvals-page__colgrid--info" />
      <col className="payment-approvals-page__colgrid payment-approvals-page__colgrid--info" />
      <col className="payment-approvals-page__colgrid payment-approvals-page__colgrid--info" />
      <col className="payment-approvals-page__colgrid payment-approvals-page__colgrid--control" />
      <col className="payment-issues-page__colgrid payment-issues-page__colgrid--account" />
    </colgroup>
  );

  const colSpan = 12;

  return (
    <div className="data-page tasks-page payment-requests-view-page payment-approvals-page payment-issues-page">
      {!canView ? (
        <div className="empty-state">Нет доступа (требуется OUTCOME_ISSUE EDIT).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Выплата по заявкам</h1>
            <div className="page-header-row__actions">
              {canEdit ? (
                <>
                  <button
                    type="button"
                    className="payment-approvals-page__approve-button"
                    onClick={() => void handleIssue()}
                    disabled={saving || selectedIds.size === 0 || defaultAccountId == null}
                  >
                    Выплачено
                  </button>
                  <button
                    type="button"
                    className="payment-approvals-page__reject-button"
                    onClick={() => void handleReject()}
                    disabled={saving || selectedIds.size === 0}
                  >
                    Отклонено
                  </button>
                </>
              ) : null}
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message">{error}</div> : null}
          {success ? <div className="success-message">{success}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen ? (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <label>
                    ID задачи
                    <input value={taskIdFilter} onChange={(e) => setTaskIdFilter(e.target.value)} />
                  </label>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статья"
                      options={articleFilterOptions}
                      selected={filterArticles}
                      onSelectedChange={setFilterArticles}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'article'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'article' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Согласующий"
                      options={approverFilterOptions}
                      selected={filterApprovers}
                      onSelectedChange={setFilterApprovers}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'approver'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'approver' : null)}
                    />
                  </div>
                  <label>
                    Дата заявки с
                    <input
                      type="date"
                      value={filterRequestDateFrom}
                      onChange={(e) => setFilterRequestDateFrom(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата заявки по
                    <input
                      type="date"
                      value={filterRequestDateTo}
                      onChange={(e) => setFilterRequestDateTo(e.target.value)}
                    />
                  </label>
                  <div className="filters-sum-block">
                    <div className="filters-sum-block__title">Сумма</div>
                    <div className="filters-sum-block__row">
                      <input
                        className="filters-sum-block__input"
                        placeholder="от"
                        value={minAmountFilter}
                        onChange={(e) => setMinAmountFilter(e.target.value)}
                      />
                      <input
                        className="filters-sum-block__input"
                        placeholder="до"
                        value={maxAmountFilter}
                        onChange={(e) => setMaxAmountFilter(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </aside>
            ) : null}

            <section className="table-content">
              {loading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table payment-requests-view-page__table payment-approvals-page__table">
                      {colgroup}
                      <thead>
                        <tr>
                          <th className="payment-approvals-page__th-check">
                            <input
                              type="checkbox"
                              checked={allVisibleSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someVisibleSelected;
                              }}
                              onChange={toggleSelectAllVisible}
                              disabled={visibleIds.length === 0 || !canEdit}
                              aria-label="Выбрать все"
                              title="Выбрать все"
                            />
                          </th>
                          <th className="sortable" onClick={() => handleSort('request_date')} role="button">
                            Дата заявки
                          </th>
                          <th className="sortable" onClick={() => handleSort('task_id')} role="button">
                            ID задачи
                          </th>
                          <th className="sortable" onClick={() => handleSort('task_short_name')} role="button">
                            Название задачи
                          </th>
                          <th className="sortable" onClick={() => handleSort('planfact_article')} role="button">
                            Статья
                          </th>
                          <th className="sortable" onClick={() => handleSort('note')} role="button">
                            Примечание
                          </th>
                          <th className="sortable col-num" onClick={() => handleSort('amount')} role="button">
                            Сумма
                          </th>
                          <th className="payment-approvals-page__info-head">План</th>
                          <th className="payment-approvals-page__info-head">Потрачено</th>
                          <th className="payment-approvals-page__info-head">Дельта</th>
                          <th className="payment-approvals-page__info-head">Контроль</th>
                          <th className="sortable" onClick={() => handleSort('payment_account')} role="button">
                            Счет
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {groups.length === 0 ? (
                          <tr>
                            <td colSpan={colSpan} className="data-table__empty">
                              Нет заявок на выдачу
                            </td>
                          </tr>
                        ) : (
                          groups.map((group) => {
                            const collapsed = collapsedGroups.has(group.approverId);
                            const groupIds = group.rows.map((row) => row.id);
                            const allGroupSelected =
                              groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));
                            const someGroupSelected =
                              groupIds.some((id) => selectedIds.has(id)) && !allGroupSelected;
                            return (
                              <React.Fragment key={group.approverId}>
                                <tr className="payment-approvals-page__group-row">
                                  <td className="payment-approvals-page__td-check">
                                    <input
                                      type="checkbox"
                                      checked={allGroupSelected}
                                      ref={(el) => {
                                        if (el) el.indeterminate = someGroupSelected;
                                      }}
                                      onChange={() => toggleGroupSelected(groupIds)}
                                      disabled={!canEdit || saving || groupIds.length === 0}
                                      aria-label={`Выбрать все заявки ${group.approverName}`}
                                      title="Выбрать все в группе"
                                    />
                                  </td>
                                  <td colSpan={colSpan - 1}>
                                    <button
                                      type="button"
                                      className="payment-approvals-page__group-toggle"
                                      onClick={() => toggleGroupCollapsed(group.approverId)}
                                    >
                                      <span aria-hidden>{collapsed ? '▸' : '▾'}</span>
                                      <span className="payment-approvals-page__group-name">
                                        {group.approverName}
                                      </span>
                                      <span className="payment-approvals-page__group-meta">
                                        {group.rows.length} шт. · {formatContractSumDisplay(group.totalAmount)}руб.
                                      </span>
                                    </button>
                                  </td>
                                </tr>
                                {!collapsed
                                  ? group.rows.map((row) => {
                                      const info = spentByKey[spentLookupKey(row.task_id, row.planfact_article)];
                                      const planValue = info?.plan ?? null;
                                      const spentValue = info?.spent ?? null;
                                      const deltaValue =
                                        planValue != null && spentValue != null ? spentValue - planValue : null;
                                      const light = getPaymentControlLight(
                                        planValue,
                                        deltaValue,
                                        Number(row.amount) || 0,
                                      );
                                      return (
                                      <tr key={row.id}>
                                        <td className="payment-approvals-page__td-check">
                                          <input
                                            type="checkbox"
                                            checked={selectedIds.has(row.id)}
                                            onChange={() => toggleRowSelected(row.id)}
                                            disabled={!canEdit || saving}
                                            aria-label={`Выбрать заявку ${row.id}`}
                                          />
                                        </td>
                                        <td>{formatDateShort(row.request_date)}</td>
                                        <td>{row.task_id}</td>
                                        <td>
                                          <div className="payment-requests-view-page__clamp-2">
                                            {displayValue(row.task_short_name)}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="payment-requests-view-page__clamp-2">
                                            {row.planfact_article}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="payment-requests-view-page__clamp-2">
                                            {displayValue(row.note)}
                                          </div>
                                        </td>
                                        <td className="col-num payment-requests-view-page__td-amount">
                                          {formatContractSumDisplay(row.amount)}
                                        </td>
                                        <td className="col-num payment-approvals-page__info">
                                          {planValue == null ? '—' : formatContractSumDisplay(planValue)}
                                        </td>
                                        <td className="col-num payment-approvals-page__info">
                                          {spentValue == null ? '—' : formatContractSumDisplay(-spentValue)}
                                        </td>
                                        <td className="col-num payment-approvals-page__info">
                                          {deltaValue == null ? '—' : formatSignedSum(deltaValue)}
                                        </td>
                                        <td className="payment-approvals-page__control">
                                          <span
                                            className={`payment-approvals-page__light payment-approvals-page__light--${light}`}
                                            title={
                                              light === 'green'
                                                ? 'В пределах плана'
                                                : light === 'red'
                                                  ? 'Перетрата относительно плана'
                                                  : 'Плана нет'
                                            }
                                            aria-label="Контроль"
                                          />
                                        </td>
                                        <td>
                                          <select
                                            className="payment-issues-page__account-select"
                                            value={accountByRowId[row.id] ?? ''}
                                            onChange={(e) => setRowAccount(row.id, Number(e.target.value))}
                                            disabled={!canEdit || saving || bankAccounts.length === 0}
                                            required
                                            aria-label={`Счёт для заявки ${row.id}`}
                                          >
                                            {bankAccounts.length === 0 ? (
                                              <option value="">Нет счетов</option>
                                            ) : (
                                              bankAccounts.map((account) => (
                                                <option key={account.id} value={account.id}>
                                                  {account.planfact_account}
                                                </option>
                                              ))
                                            )}
                                          </select>
                                        </td>
                                      </tr>
                                      );
                                    })
                                  : null}
                              </React.Fragment>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer payment-requests-view-page__table payment-approvals-page__table">
                      {colgroup}
                      <tfoot>
                        <tr>
                          <td />
                          <td>Итого: {sortedRows.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td className="col-num payment-requests-view-page__td-amount">
                            {formatContractSumDisplay(totalAmount)}
                          </td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentIssuesPage;
